export type ItemizedFee = {
    title: string | JSX.Element;
    total: number;
};

export type FeeDetails = {
    totalFee: number;
    totalPages?: number;
    itemizedFees: ItemizedFee[];
};

type FeeCalculationParams = {
    standardSizeSelection?: string;
    doubleDocumentSelection?: string;
    doubleDocumentTitles?: string;
    surveyMonumentFeeSelection?: string;
    documentTransferTaxSelection?: string;
    documentTransferTaxPrice?: string;
    areaSelection?: string;
    sb2RcmFeeSelection?: string;
    prelimChangeOfOwnershipFeeSelection?: string;
    totalPages?: string;
    baseFee: number;
};

type ROJFeeCalculationParams = {
    standardSizeSelection?: string;
    doubleDocumentSelection?: string;
    doubleDocumentTitles?: string;
    sb2RcmFeeSelection?: string;
    totalPages?: string;
    baseFee: number;
};

type UCCFeeCalculationParams = {
    standardSizeSelection?: string;
    sb2RcmFeeSelection?: string;
    totalPages?: string;
    baseFee: number;
};

export const calculateROJFeeDetails = (params: ROJFeeCalculationParams): FeeDetails => {
    const itemizedFees: ItemizedFee[] = [];
    let totalFee = params.baseFee;
    itemizedFees.push({
        title: "Release of Judgment Lien (Child Support)",
        total: params.baseFee,
    });

    return {
        totalFee,
        itemizedFees,
    };
};

export const calculateUCCFeeDetails = (params: UCCFeeCalculationParams): FeeDetails => {
    const itemizedFees: ItemizedFee[] = [];
    const pages = !isNaN(Number(params.totalPages)) ? Number(params.totalPages) : 1;
    let totalFee = 0;

    if (pages > 0) {
        if (pages < 3) {
            totalFee += 10;
            itemizedFees.push({
                title: (
                    <>
                        UCC Filing <em>(1 - 2 Pages)</em>
                    </>
                ),
                total: 10,
            });
        } else {
            totalFee += 20;
            itemizedFees.push({
                title: (
                    <>
                        UCC Filing <em>(3 Or More Pages)</em>
                    </>
                ),
                total: 20,
            });
        }
    }

    if (params?.standardSizeSelection === "1") {
        totalFee += pages * 3;
        itemizedFees.push({
            title: (
                <>
                    Non-Standard Page Size Fee{" "}
                    <em>
                        ({pages} {pages > 1 ? "pages" : "page"} x $3.00)
                    </em>
                </>
            ),
            total: pages * 3,
        });
    }

    if (params?.sb2RcmFeeSelection === "4") {
        totalFee += 77;
        itemizedFees.push({
            title: "Building Homes and Jobs Act Fee and Restrictive Covenant Modification Fee",
            total: 77,
        });
    }

    return {
        totalFee,
        itemizedFees,
        totalPages: pages,
    };
};

export const calculateFeeDetails = (params: FeeCalculationParams): FeeDetails => {
    const itemizedFees: ItemizedFee[] = [];
    const pages = !isNaN(Number(params.totalPages)) ? Number(params.totalPages) : 1;
    let totalFee = 0;

    if (pages > 0) {
        totalFee += params.baseFee;

        itemizedFees.push({
            title: "First Page Fee",
            total: params.baseFee,
        });

        if (pages > 1) {
            totalFee += Math.max(0, pages - 1) * 3;
            itemizedFees.push({
                title: (
                    <>
                        Additional Pages Fee{" "}
                        <em>
                            ({pages - 1} additional {pages > 1 ? "pages" : "page"} x $3.00)
                        </em>
                    </>
                ),
                total: Math.max(0, pages - 1) * 3,
            });
        }
    }

    if (params?.standardSizeSelection === "1") {
        totalFee += pages * 3;
        itemizedFees.push({
            title: (
                <>
                    Non-Standard Page Size Fee{" "}
                    <em>
                        ({pages} {pages > 1 ? "pages" : "page"} x $3.00)
                    </em>
                </>
            ),
            total: pages * 3,
        });
    }

    if (params?.doubleDocumentSelection === "0") {
        const numTitles = !isNaN(Number(params.doubleDocumentTitles)) ? Number(params.doubleDocumentTitles) : 0;
        if (numTitles > 1) {
            totalFee += (numTitles - 1) * 14;
            itemizedFees.push({
                title: (
                    <>
                        Double Document/Combined Document Fee{" "}
                        <em>
                            ({numTitles - 1} additional {numTitles > 2 ? "titles" : "title"} x $14.00)
                        </em>
                    </>
                ),
                total: (numTitles - 1) * 14,
            });
        }
    }

    if (params?.surveyMonumentFeeSelection === "0") {
        totalFee += 10;
        itemizedFees.push({
            title: "Survey Monument Fee",
            total: 10,
        });
    }

    if (params?.documentTransferTaxSelection === "0" && params?.documentTransferTaxPrice) {
        const dttPrice = !isNaN(Number(params.documentTransferTaxPrice)) ? Number(params.documentTransferTaxPrice) : 0;

        totalFee += Math.ceil(dttPrice / 500) * 0.55;
        itemizedFees.push({
            title: "Documentary Transfer Tax (County)",
            total: Math.ceil(dttPrice / 500) * 0.55,
        });

        if (params?.areaSelection === "Santa Rosa" || params?.areaSelection === "Petaluma") {
            totalFee += Math.ceil(dttPrice / 1000) * 2;
            itemizedFees.push({
                title: `Documentary Transfer Tax (${params.areaSelection})`,
                total: Math.ceil(dttPrice / 1000) * 2,
            });
        }
    }

    if (params?.sb2RcmFeeSelection === "4") {
        if (params?.doubleDocumentSelection === "0") {
            const numTitles = !isNaN(Number(params.doubleDocumentTitles)) ? Number(params.doubleDocumentTitles) : 0;
            if (numTitles > 1) {
                totalFee += 77 * numTitles;
                itemizedFees.push({
                    title: (
                        <>
                            Building Homes and Jobs Act Fee and Restrictive Covenant Modification Fee{" "}
                            <em>({numTitles} titles x $77.00)</em>
                        </>
                    ),
                    total: 77 * numTitles,
                });
            } else {
                totalFee += 77;
                itemizedFees.push({
                    title: "Building Homes and Jobs Act Fee and Restrictive Covenant Modification Fee",
                    total: 77,
                });
            }
        } else {
            totalFee += 77;
            itemizedFees.push({
                title: "Building Homes and Jobs Act Fee and Restrictive Covenant Modification Fee",
                total: 77,
            });
        }
    }

    if (params?.prelimChangeOfOwnershipFeeSelection === "1") {
        totalFee += 20;
        itemizedFees.push({
            title: "Preliminary Change of Ownership Fee",
            total: 20,
        });
    }

    return {
        totalFee,
        itemizedFees,
        totalPages: pages,
    };
};
